import { OwaDate, addMonths, differenceInDays } from 'owa-datetime';
import { getFolderIdByCalendarID } from 'owa-timestream-asset-sdk-calendars';
import { initializeAgenda } from 'owa-agenda-events-list';
import { loadDayGrid } from 'owa-day-grid';
import { CalendarViewType } from '../schema';
import { assertNever } from 'owa-assert';
import { BoardCalendarAssetEntrySource } from '../constants';
import { getStartOfWeek } from '../utils/dateUtils';
import { getFluentDateRangeType, NUM_DAYS_WEEK_VIEW } from '../utils/calendarViewTypeUtils';
import type { PerformanceDatapoint } from 'owa-analytics';
import { QUICK_COMPOSE_ENTRY_SOURCE_BOARD_CALENDAR_ASSET } from 'owa-calendar-helpers-types/lib/ComposeMetricTypes';
import { CalendarEventsFilters } from 'owa-calendar-events-filter';

export function initializeView(
    scenarioId: string,
    selectedView: CalendarViewType,
    selectedDate: OwaDate,
    selectedCalendars: string[],
    numDaysInDayRange: number = 1,
    assetPerfDatapoint?: React.RefObject<PerformanceDatapoint | undefined>
) {
    switch (selectedView) {
        case CalendarViewType.Agenda:
            initializeAgendaView(scenarioId, selectedDate, selectedCalendars, assetPerfDatapoint);
            break;
        case CalendarViewType.Day:
        case CalendarViewType.Week:
        case CalendarViewType.WorkWeek:
            initializeDayGridView(
                scenarioId,
                selectedView,
                selectedDate,
                selectedCalendars,
                numDaysInDayRange
            );
            break;
        case CalendarViewType.Month:
            // relies on `useGetCalendarEvents` hook called within month view
            break;
        default:
            assertNever(selectedView);
    }
}

// helper function - do not export
function initializeAgendaView(
    scenarioId: string,
    selectedDate: OwaDate,
    selectedCalendars: string[],
    assetPerfDatapoint: React.RefObject<PerformanceDatapoint | undefined> | undefined
) {
    // get number of days in the next two months
    const numberOfDays = differenceInDays(addMonths(selectedDate, 2), selectedDate);

    initializeAgenda(
        selectedDate,
        selectedCalendars,
        scenarioId,
        false, // TODO VSO 92468: Add ability to see tasks in agenda view of canvas calendar widgets
        false /** shouldShowDeclinedEvents TODO VSO 51643: Add Time Panel setting for showing declined meetings */,
        BoardCalendarAssetEntrySource,
        {
            maxDays: numberOfDays,
            showDateRangeFooter: true,
        },
        QUICK_COMPOSE_ENTRY_SOURCE_BOARD_CALENDAR_ASSET,
        [CalendarEventsFilters.All], // currentFilters
        assetPerfDatapoint
    );
}

// helper function - do not export
function initializeDayGridView(
    scenarioId: string,
    selectedView: CalendarViewType,
    selectedDate: OwaDate,
    selectedCalendarIds: string[],
    numDaysInDayRange: number | undefined
) {
    const calendarFolderIds = selectedCalendarIds.reduce(
        (accumulatedCalendarFolderIds: string[], currentCalendarId: string) => {
            const currentCalendarFolderId = getFolderIdByCalendarID(currentCalendarId);
            if (currentCalendarFolderId) {
                accumulatedCalendarFolderIds.push(currentCalendarFolderId);
            }
            return accumulatedCalendarFolderIds;
        },
        []
    );
    loadDayGrid(
        scenarioId,
        selectedView == CalendarViewType.Day
            ? selectedDate
            : getStartOfWeek(selectedDate) /* initialDate */,
        null /* focusTimeDateRange */,
        calendarFolderIds /* folderIds */,
        true /* enableDragDrop */,
        true /* enableUpNext */,
        selectedView === CalendarViewType.Day
            ? numDaysInDayRange
            : NUM_DAYS_WEEK_VIEW /* numDaysInDayRange */,
        getFluentDateRangeType(selectedView) /* dateRangeType */,
        1 // VSO 131235: update board view with setting to customize time scale
    );
}
